import React, {useEffect, useRef, useState} from 'react';

import Link from './Link';
import {CaretIcon} from './svg';
import {cx} from '../util';

const crumbClasses = (isCurrent) => cx(
  'whitespace-nowrap transition-colors',
  isCurrent ? 'font-medium text-gray-600' : 'text-gray-500 hover:text-gray-700',
);

/**
 * @param {boolean} [props.condensed] Collapse long trails to first two crumbs
 *   + last, with the hidden middle behind a clickable ellipsis menu. Also
 *   drops the scroll container, which the dropdown couldn't escape.
 */
const Breadcrumbs = ({items = [], condensed = false, className}) => {
  const collapsed = condensed && items.length > 3;
  const hiddenItems = collapsed ? items.slice(2, -1) : [];
  const crumbs = collapsed
    ? [...items.slice(0, 2), {ellipsis: true}, items[items.length - 1]]
    : items;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocumentClick = ({target}) => {
      if (menuRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <nav
      aria-label="Breadcrumb"
      className={cx(
        'flex items-center',
        !condensed && 'overflow-scroll scrollbar-hidden',
        className,
      )}
    >
      <ol className="flex min-w-0 items-center space-x-3">
        {crumbs.map(({
          url, name, truncate, ellipsis,
        }, i) => (
          <li
            key={ellipsis ? '__ellipsis__' : `${url}_${name}`}
            // Only crumbs marked truncate may compress; the rest hold their
            // width, otherwise their text overflows onto the next caret.
            className={cx('flex items-center', truncate ? 'min-w-0' : 'shrink-0')}
          >
            {i > 0 && (
              <CaretIcon
                height={9}
                width={6}
                fill="#999"
                className="mr-3 shrink-0 -rotate-180"
                aria-hidden="true"
              />
            )}
            {ellipsis && (
              <span ref={menuRef} className="relative">
                <button
                  type="button"
                  aria-label="Show all breadcrumbs"
                  aria-expanded={menuOpen}
                  aria-haspopup="true"
                  onClick={() => setMenuOpen((prev) => !prev)}
                  className="cursor-pointer rounded px-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                >
                  …
                </button>
                {menuOpen && (
                  <div className="absolute top-full left-0 z-40 mt-2 min-w-44 rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl shadow-gray-900/10">
                    {hiddenItems.map((item) => (
                      <Link
                        key={`${item.url}_${item.name}`}
                        href={item.url}
                        onClick={() => setMenuOpen(false)}
                        className="block max-w-64 truncate rounded-lg px-2.5 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </span>
            )}
            {!ellipsis && (i === crumbs.length - 1 ? (
              <span
                aria-current="page"
                className={cx(crumbClasses(true), truncate && 'truncate max-w-[180px]')}
              >
                {name}
              </span>
            ) : (
              <Link
                href={url}
                className={cx(crumbClasses(false), truncate && 'truncate max-w-[180px]')}
              >
                {name}
              </Link>
            ))}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
